// src/components/RequireAdminOrManager.js
// Route guard for admin-or-manager pages (Settings → Scheduled Reports,
// /templates/create, /templates/edit/:templateId). Mirrors RequireAdmin
// but accepts `role === 'admin'` OR `role === 'manager'`.
//
// See RequireAdmin.js for the design rationale (self-heal path, denied
// redirects, why we decode the token instead of reading subscriberRole
// from localStorage). The only meaningful difference here is the role
// check.

import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../apiConfig';
import { decodeJwtPayload } from '../utils/jwtPayload';

const ALLOWED = new Set(['admin', 'manager']);

function decodeRoleClaim(token) {
  if (!token) return null;
  try {
    const payload = decodeJwtPayload(token) || {};
    return payload.role || null;
  } catch {
    return null;
  }
}

function decodeSubscriberClaim(token) {
  if (!token) return null;
  try {
    const payload = decodeJwtPayload(token) || {};
    return payload.subscriber_id || null;
  } catch {
    return null;
  }
}

function deniedRedirectPath(role) {
  if (role === 'customer') return '/customer-portal';
  return '/tasks';
}

export default function RequireAdminOrManager({ children }) {
  const { currentUser, loading, getIdToken } = useAuth();
  const [status, setStatus] = useState('checking');
  const [deniedRole, setDeniedRole] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!currentUser) {
      setStatus('denied');
      return;
    }

    let cancelled = false;

    (async () => {
      let token = await getIdToken(false);
      let role = decodeRoleClaim(token);
      if (ALLOWED.has(role)) {
        if (!cancelled) setStatus('allowed');
        return;
      }

      const subscriberId = decodeSubscriberClaim(token)
        || localStorage.getItem('subscriberId');
      if (!role && subscriberId) {
        try {
          const resp = await fetch(`${API_BASE_URL}/api/v1/auth/set-custom-claims`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ subscriber_id: subscriberId }),
          });
          if (resp.ok) {
            const refreshed = await getIdToken(true);
            role = decodeRoleClaim(refreshed);
            if (ALLOWED.has(role)) {
              if (!cancelled) setStatus('allowed');
              return;
            }
          }
        } catch (e) {
          console.warn('RequireAdminOrManager self-heal failed:', e);
        }
      }

      if (!cancelled) {
        setDeniedRole(role);
        setStatus('denied');
      }
    })();

    return () => { cancelled = true; };
  }, [currentUser, loading, getIdToken]);

  if (loading || status === 'checking') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'denied') {
    return <Navigate to={currentUser ? deniedRedirectPath(deniedRole) : '/login'} replace />;
  }

  return <>{children}</>;
}
